import { useEffect, useMemo, useRef, useState } from 'react'
import { useKBar, useRegisterActions, Action, VisualState } from 'kbar'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PersonIcon from '@mui/icons-material/Person'
import { z } from 'zod'

import { useDebounce } from '@hapana/shared/web/hooks/useDebounce'
import { resolve } from '@hapana/shared/web/services/gqty'
import {
  clientSearchResultSchema,
  type ClientSearchResult,
} from '@hapana/shared/schemas/users/client-schemas'
import useSiteStore from '../../stores/site.store'
import { getDefaultClientTabParam } from '../../pages/clients/clients.constants'

const MAX_CLIENT_RESULTS = 5
const SEARCH_DEBOUNCE_MS = 300
const MIN_SEARCH_LENGTH = 2
const MAX_SEARCH_LENGTH = 64

const EMPTY_ACTIONS: Action[] = []

const KBarClientSearch = () => {
  const navigate = useNavigate()
  const { t } = useTranslation('core')
  const { siteId: paramSiteId } = useParams()
  const currentSite = useSiteStore((state) => state.currentSite)
  const siteId = paramSiteId ?? currentSite?.siteId

  const [clients, setClients] = useState<ClientSearchResult[]>([])
  const [defaultClientTab, setDefaultClientTab] = useState<string | null>(null)
  const requestIdRef = useRef(0)

  const { searchQuery, visualState } = useKBar((state) => ({
    searchQuery: state.searchQuery,
    visualState: state.visualState,
  }))

  const isKBarOpen = visualState === VisualState.showing || visualState === VisualState.animatingIn

  const debouncedSearch = useDebounce(searchQuery, SEARCH_DEBOUNCE_MS)

  useEffect(() => {
    if (!siteId) {
      return
    }
    let isMounted = true
    resolve(
      ({ query }) => {
        const site = query.site({ siteId })
        return site?.settings?.location?.operations?.systemPreferences?.defaultClientTab
      },
      { cachePolicy: 'cache-first' }
    )
      .then((tab) => {
        if (isMounted) {
          setDefaultClientTab(tab ?? null)
        }
      })
      // The command palette must not surface a failed lookup — the tab param is
      // optional, and an unsettled rejection here reached the window as an
      // UnhandledPromiseRejection.
      .catch(() => {
        if (isMounted) {
          setDefaultClientTab(null)
        }
      })
    return () => {
      isMounted = false
    }
  }, [siteId])

  useEffect(() => {
    if (!isKBarOpen) {
      requestIdRef.current++
      setClients((prev) => (prev.length === 0 ? prev : []))
      return
    }

    const normalizedSearch = debouncedSearch?.trim().slice(0, MAX_SEARCH_LENGTH)

    if (!siteId || !normalizedSearch || normalizedSearch.length < MIN_SEARCH_LENGTH) {
      requestIdRef.current++
      setClients((prev) => (prev.length === 0 ? prev : []))
      return
    }

    const currentRequestId = ++requestIdRef.current

    resolve(
      ({ query }) => {
        const clientsQuery = query.clients({
          search: normalizedSearch,
          pageSize: MAX_CLIENT_RESULTS,
          siteId,
        })

        const rawResults =
          clientsQuery?.results?.map((r) => ({
            id: r.row?.id,
            firstName: r.row?.firstName,
            lastName: r.row?.lastName,
            name: r.row?.name,
            email: r.row?.email,
            mobilePhoneNumber: r.row?.mobilePhoneNumber,
          })) ?? []

        return z.array(clientSearchResultSchema).parse(rawResults.filter((c) => c.id))
      },
      { cachePolicy: 'no-cache' }
    )
      .then((result) => {
        if (currentRequestId === requestIdRef.current) {
          setClients(result)
        }
      })
      // A failed search clears the results rather than rejecting unhandled; the
      // next keystroke retries.
      .catch(() => {
        if (currentRequestId === requestIdRef.current) {
          setClients((prev) => (prev.length === 0 ? prev : []))
        }
      })

    return () => {
      requestIdRef.current++
    }
  }, [debouncedSearch, siteId, isKBarOpen])

  // With no results to register, always return the same reference so siteId/navigate/t
  // churn while kbar is closed can't re-register actions and loop (React #185). See OC-41.
  const clientActions: Action[] = useMemo(() => {
    if (!isKBarOpen || clients.length === 0) {
      return EMPTY_ACTIONS
    }

    return clients.map((client) => ({
      id: `client:${siteId}:${client.id}`,
      name:
        (client.name ?? `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim()) ||
        t('kbar.client.unknownName'),
      subtitle: client.email ?? client.mobilePhoneNumber ?? '',
      section: t('kbar.sections.clients'),
      icon: <PersonIcon fontSize="small" />,
      keywords: [client.firstName, client.lastName, client.email, client.mobilePhoneNumber]
        .filter(Boolean)
        .join(' '),
      perform: () => {
        const basePath = siteId ? `/s/${siteId}` : ''
        const tabParam = getDefaultClientTabParam(defaultClientTab)
        navigate(`${basePath}/clients/${client.id}${tabParam}`)
      },
      priority: -1,
    }))
  }, [clients, siteId, defaultClientTab, navigate, t, isKBarOpen])

  // Depend on the inputs that actually change the action list, not on the array's own identity.
  // `t` and `navigate` are not referentially stable, so keying off `clientActions` re-registered
  // on any render where either changed identity; each registration sets kbar state, which renders
  // again, which produces another new array — React #185, Maximum update depth exceeded (OC-1305).
  useRegisterActions(clientActions, [clients, siteId, defaultClientTab, isKBarOpen])

  return null
}

export default KBarClientSearch
